/* eslint-disable max-lines-per-function */
import { useCallback, useMemo } from 'react';

import qs from 'qs';
import { useLocation } from 'react-router';

import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';
import { useUtilityContext } from '@change-corgi/core/react/utilityContext';
import { getLocation } from '@change-corgi/core/window';

import { useStarterDashboardV2PageData } from 'src/app/pages/starterDashboardV2/pageContext';
import { useFlyerDownloadModalContext } from 'src/app/pages/starterDashboardV2/shared/components';
import {
	COMPLETE_TASK_ERROR_MAP,
	useCompleteTask,
	useTriggerSharePetitionModal,
} from 'src/app/pages/starterDashboardV2/shared/hooks';
import { useCurrentUserId, useSessionUuid } from 'src/app/shared/hooks/session';
import { useShortUrls } from 'src/app/shared/hooks/shortUrls';
import { shareNativeExtended } from 'src/app/shared/share/mobileNativeShare';

import { useIsMobile } from '../../../../../../../../hooks/useIsMobile';
import { useShareUrl } from '../../../../../GrowPetition/components/ShareYourPetition/components/ShareUrl/hooks/useShareUrl';
import type { Task } from '../../../../types';

type GetShareUrlWithUtmParamsParams = {
	starterVanityUrl: string | null | undefined;
	slug: string;
	resolveUrl: (path: string) => string;
	currentUserId: string | undefined;
	sessionUuid: string;
};

function getShareUrlWithUtmParams({
	starterVanityUrl,
	slug,
	resolveUrl,
	currentUserId,
	sessionUuid,
}: GetShareUrlWithUtmParamsParams): string {
	// Construct base URL with vanity URL preference
	const vanityUrl = starterVanityUrl ? `${resolveUrl('/')}${starterVanityUrl}` : null;

	// If we have a vanity URL, use it; otherwise build from slug
	if (vanityUrl) {
		// For vanity URLs, add UTM params directly
		const trackParams = qs.stringify(
			{
				utm_campaign: 'starter_dashboard',
				utm_content: 'next_steps',
				utm_medium: 'mobileNativeShare',
				utm_source: 'share_petition',
				utm_term: 'starter_dashboard_web',
				utm_recruited_by_id: sessionUuid,
				...(currentUserId ? { utm_recruiter: currentUserId } : {}),
			},
			{ encodeValuesOnly: true, addQueryPrefix: true },
		);
		return `${vanityUrl}${trackParams}`;
	}

	// For non-vanity URLs, build from slug with all params
	const trackParams = qs.stringify(
		{
			...(currentUserId ? { recruiter: currentUserId } : {}),
			recruited_by_id: sessionUuid,
			utm_source: 'share_petition',
			utm_campaign: 'starter_dashboard',
			utm_content: 'next_steps',
			utm_medium: 'mobileNativeShare',
			utm_term: 'starter_dashboard_web',
			utm_recruited_by_id: sessionUuid,
			...(currentUserId ? { utm_recruiter: currentUserId } : {}),
		},
		{ encodeValuesOnly: true, addQueryPrefix: true },
	);
	return resolveUrl(`/p/${slug}${trackParams}`);
}

function navigateToProfile(slug: string, navigate: ReturnType<typeof useNavigate>) {
	const redirectTo = encodeURIComponent(`/p/${slug}/dashboard/home`);
	navigate(`/account_settings/profile?redirectTo=${redirectTo}`);
}

function navigateToGroupRecommendations(slug: string, navigate: ReturnType<typeof useNavigate>) {
	navigate(`/p/${slug}/dashboard/share#recommendations-section`);
}

function navigateToReadyToSharePosts(
	slug: string,
	navigate: ReturnType<typeof useNavigate>,
	platform?: 'facebook' | 'instagram' | 'reddit' | 'facebookgroups',
) {
	const location = getLocation();
	const searchParams = new URLSearchParams(location.search);
	if (platform) {
		searchParams.set('platform', platform);
	}
	const queryString = searchParams.toString();
	const url = `/p/${slug}/dashboard/share${queryString ? `?${queryString}` : ''}#ready-to-share-posts`;
	navigate(url);
}

function navigateToPostUpdate(slug: string, navigate: ReturnType<typeof useNavigate>) {
	navigate(`/p/${encodeURIComponent(slug)}/dashboard/updates`);
}

export function useGetAction() {
	const {
		data: {
			petition,
			featureConfigs: { multipleQrCodeFlyersEnabled },
		},
		actions: { refreshData },
	} = useStarterDashboardV2PageData();
	const showMultiDownloadFlyersTask = multipleQrCodeFlyersEnabled;
	const isMobile = useIsMobile();
	const track = useTracking();

	const { openModal: openFlyerDownloadModal } = useFlyerDownloadModalContext();
	const triggerSharePetitionModal = useTriggerSharePetitionModal();

	const navigate = useNavigate();
	const { pathname, search } = useLocation();
	const { resolveUrl } = useUtilityContext();
	const currentUserId = useCurrentUserId();
	const sessionUuid = useSessionUuid();
	const { mutate: completeTask } = useCompleteTask({
		onError: (error) => {
			if (error === COMPLETE_TASK_ERROR_MAP.UNAUTHORIZED_ERROR.code) {
				const params = encodeURIComponent(`${pathname}${search}`);
				navigate(`/login_or_join?redirectTo=${params}`);
			}
		},
		completionType: 'manual',
		version: 'new_12_2025',
	});

	const { data, actions } = useShareUrl();

	// Build share URL with UTM parameters, then shorten it
	const longShareUrl = useMemo(
		() =>
			getShareUrlWithUtmParams({
				starterVanityUrl: petition.starterVanityUrl,
				slug: petition.slug,
				resolveUrl,
				currentUserId,
				sessionUuid,
			}),
		[petition.starterVanityUrl, petition.slug, resolveUrl, currentUserId, sessionUuid],
	);

	const shortUrls = useShortUrls({ share: longShareUrl });
	const shareUrlWithUtmParams = shortUrls.share;

	return useCallback(
		({ id, name, status = 'ACTIVE' }: { id: Task['id']; name: Task['name']; status?: Task['status'] }) => {
			switch (name) {
				case 'complete_profile': {
					return async () => {
						void track('petition_manage_tasks_cta_click', { task: name, version: 'new_12_2025' });
						navigateToProfile(petition.slug, navigate);
						return Promise.resolve(true);
					};
				}
				case 'customize_link': {
					return async () => {
						void track('petition_manage_tasks_cta_click', { task: name, version: 'new_12_2025' });

						if (!data.canEditUrl) {
							return true;
						}

						await actions.onEditUrl();
						// DO NOT COMPLETE - task is completed asynchronously in backend service
						refreshData();
						return true;
					};
				}
				case 'share_friends': {
					return async () => {
						void track('petition_manage_tasks_cta_click', { task: name, version: 'new_12_2025' });
						if (isMobile) {
							await shareNativeExtended(
								{ url: shareUrlWithUtmParams },
								{
									trackingEvents: { success: 'share_friends_success', failure: 'share_friends_failure' },
									trackingData: { task: name },
								},
								track,
							);
							if (status !== 'COMPLETED') {
								await completeTask(id, name);
								refreshData();
							}
						} else {
							await triggerSharePetitionModal();
						}
						return Promise.resolve(true);
					};
				}
				case 'share_communities': {
					return async () => {
						void track('petition_manage_tasks_cta_click', { task: name, version: 'new_12_2025' });
						navigateToGroupRecommendations(petition.slug, navigate);
						return Promise.resolve(true);
					};
				}
				case 'share_facebook': {
					return async () => {
						void track('petition_manage_tasks_cta_click', { task: name, version: 'new_12_2025' });
						navigateToReadyToSharePosts(petition.slug, navigate, 'facebook');
						return Promise.resolve(true);
					};
				}
				case 'share_instagram': {
					return async () => {
						void track('petition_manage_tasks_cta_click', { task: name, version: 'new_12_2025' });
						navigateToReadyToSharePosts(petition.slug, navigate, 'instagram');
						return Promise.resolve(true);
					};
				}
				case 'download_flyer': {
					return async () => {
						void track('petition_manage_tasks_cta_click', { task: name, version: 'new_12_2025' });
						if (showMultiDownloadFlyersTask) {
							openFlyerDownloadModal('starter_dashboard_task');
							refreshData();
							return Promise.resolve(true);
						}

						if (status !== 'COMPLETED') {
							await completeTask(id, name);
							refreshData();
						}
						return Promise.resolve(true);
					};
				}
				case 'thank_supporters': {
					return async () => {
						void track('petition_manage_tasks_cta_click', { task: name, version: 'new_12_2025' });
						navigateToPostUpdate(petition.slug, navigate);
						return Promise.resolve(true);
					};
				}
				case 'promote_basic': {
					return async () => {
						void track('petition_manage_tasks_cta_click', { task: name, version: 'new_12_2025' });
						// We don't complete the task here, it's completed automatically in the backend service
						// when the user visits the promote page
						navigate(`/p/${petition.slug}/promote`);
						refreshData();
						return Promise.resolve(true);
					};
				}
				default:
					return async () => Promise.resolve(true);
			}
		},
		[
			petition.slug,
			shareUrlWithUtmParams,
			refreshData,
			showMultiDownloadFlyersTask,
			openFlyerDownloadModal,
			completeTask,
			navigate,
			triggerSharePetitionModal,
			isMobile,
			track,
			data,
			actions,
		],
	);
}
